import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  RootCategory,
  SecurityQuestionDTO,
  SupportedLocation,
  createUserDeletionRequest,
  createUserSuspensionRequest,
  fetchHelperCategoryPreference,
  fetchRootTaskCategories,
  fetchSecurityQuestions,
  fetchSupportedLocations,
  updateHelperCategoryPreference,
  updateSecurityQuestions,
} from '../../services/settingsService';
import { startEmailVerification, startPhoneVerification, verifyEmail, verifyPhone } from '../../services/verificationService';
import '../../styles/Settings.css';

type SettingsTab =
  | 'account'
  | 'password'
  | 'security'
  | 'verifyEmail'
  | 'verifyPhone'
  | 'notifications'
  | 'preferences'
  | 'supportedLocations'
  | 'appDetails'
  | 'deactivate';

type Message = { type: 'success' | 'error'; text: string } | null;
type AccountAction = 'SUSPEND' | 'DELETE';

const reasonOptions = [
  { label: 'Privacy concerns', value: 'PRIVACY_CONCERNS' },
  { label: 'Too many notifications', value: 'TOO_MANY_NOTIFICATIONS' },
  { label: 'Not using the app enough', value: 'NOT_USING_APP' },
  { label: 'Found an alternative service', value: 'FOUND_ALTERNATIVE' },
  { label: 'Temporary break', value: 'TEMPORARY_BREAK' },
  { label: 'Technical issues', value: 'TECHNICAL_ISSUES' },
  { label: 'Safety or trust concerns', value: 'SAFETY_CONCERNS' },
  { label: 'Poor experience', value: 'POOR_EXPERIENCE' },
  { label: 'Other', value: 'OTHER' },
];

const Settings = () => {
  const navigate = useNavigate();
  const { user, updateProfile, updatePassword, refreshAuthState, refreshProfile, getAccessToken, logout } = useAuth();
  const userId = user?.id || '';
  const isHelper = String(user?.currentAppRole || '').toUpperCase() === 'HELPER';

  const [activeTab, setActiveTab] = useState<SettingsTab>('account');
  const [message, setMessage] = useState<Message>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [accountDetails, setAccountDetails] = useState({ username: '', email: '', phone: '' });
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, next: false, confirm: false });
  const [emailCode, setEmailCode] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [emailOtpToken, setEmailOtpToken] = useState('');
  const [phoneOtpToken, setPhoneOtpToken] = useState('');

  const [categories, setCategories] = useState<RootCategory[]>([]);
  const [categoryPrefs, setCategoryPrefs] = useState<Record<string, boolean>>({});
  const [savingCategory, setSavingCategory] = useState<Record<string, boolean>>({});

  const [language, setLanguage] = useState('English');
  const [theme, setTheme] = useState('Light');
  const [retention, setRetention] = useState('N/A');

  const [securityQuestions, setSecurityQuestions] = useState<SecurityQuestionDTO[]>([]);
  const [securityPayload, setSecurityPayload] = useState({
    question1Id: '',
    answer1: '',
    question2Id: '',
    answer2: '',
    question3Id: '',
    answer3: '',
    password: '',
  });
  const [showAnswers, setShowAnswers] = useState(false);

  const [locations, setLocations] = useState<SupportedLocation[]>([]);
  const [locationSearch, setLocationSearch] = useState('');

  const [selectedAction, setSelectedAction] = useState<AccountAction | null>(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const showMessage = (next: Message) => {
    setMessage(next);
    if (next) window.setTimeout(() => setMessage(null), 4000);
  };

  useEffect(() => {
    if (!user) return;
    setAccountDetails({
      username: user.username || '',
      email: user.email || '',
      phone: user.phone || user.contactDetails?.mobilePhone || '',
    });
  }, [user]);

  const loadCategoryNotifications = useCallback(async () => {
    if (!isHelper || !userId) return;
    try {
      const rootCategories = await fetchRootTaskCategories();
      setCategories(rootCategories);
      const prefs = await Promise.all(
        rootCategories.map(async (category) => {
          const pref = await fetchHelperCategoryPreference(userId, category.id);
          return [category.id, !!pref?.enabled] as const;
        })
      );
      setCategoryPrefs(Object.fromEntries(prefs));
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to load notification categories.' });
    }
  }, [isHelper, userId]);

  const loadSecurityQuestions = useCallback(async () => {
    try {
      setSecurityQuestions(await fetchSecurityQuestions());
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to load security questions.' });
    }
  }, []);

  const loadSupportedLocations = useCallback(async () => {
    setIsLoading(true);
    try {
      const rows = await fetchSupportedLocations();
      rows.sort((a, b) => `${a.country}|${a.state}|${a.city}|${a.county}`.localeCompare(`${b.country}|${b.state}|${b.city}|${b.county}`));
      setLocations(rows);
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to load supported locations.' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'notifications') loadCategoryNotifications();
    if (activeTab === 'security') loadSecurityQuestions();
    if (activeTab === 'supportedLocations') loadSupportedLocations();
  }, [activeTab, loadCategoryNotifications, loadSecurityQuestions, loadSupportedLocations]);

  const handleAccountUpdate = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      await updateProfile(accountDetails);
      showMessage({ type: 'success', text: 'Account details updated successfully.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to update account details.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordUpdate = async (event: FormEvent) => {
    event.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (passwordData.newPassword.length < 8) {
      showMessage({ type: 'error', text: 'Password must be at least 8 characters.' });
      return;
    }

    setIsLoading(true);
    try {
      await updatePassword(passwordData.currentPassword, passwordData.newPassword, passwordData.confirmPassword);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showMessage({ type: 'success', text: 'Password updated successfully.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to update password.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmailVerification = async () => {
    setIsLoading(true);
    try {
      const token = getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const response = await startEmailVerification(token);
      setEmailOtpToken(response.otpToken || '');
      showMessage({ type: 'success', text: 'Verification code sent to your email.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to send verification code.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    setIsLoading(true);
    try {
      const token = getAccessToken();
      if (!token || !emailOtpToken) throw new Error('Verification session missing');
      await verifyEmail(emailOtpToken, emailCode, token);
      await refreshProfile();
      refreshAuthState();
      showMessage({ type: 'success', text: 'Email verified successfully.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Invalid verification code.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendPhoneVerification = async () => {
    setIsLoading(true);
    try {
      const token = getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const response = await startPhoneVerification(token, accountDetails.phone || user?.phone);
      setPhoneOtpToken(response.otpToken || '');
      showMessage({ type: 'success', text: 'Verification code sent to your phone.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to send verification code.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    setIsLoading(true);
    try {
      const token = getAccessToken();
      if (!token || !phoneOtpToken) throw new Error('Verification session missing');
      await verifyPhone(phoneOtpToken, phoneCode, token);
      await refreshProfile();
      refreshAuthState();
      showMessage({ type: 'success', text: 'Phone verified successfully.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Invalid verification code.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleCategory = async (categoryId: string, enabled: boolean) => {
    if (!userId) return;
    setCategoryPrefs((prev) => ({ ...prev, [categoryId]: enabled }));
    setSavingCategory((prev) => ({ ...prev, [categoryId]: true }));
    try {
      const pref = await updateHelperCategoryPreference(userId, categoryId, enabled);
      setCategoryPrefs((prev) => ({ ...prev, [categoryId]: !!pref.enabled }));
    } catch (error) {
      setCategoryPrefs((prev) => ({ ...prev, [categoryId]: !enabled }));
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Could not update notification preference.' });
    } finally {
      setSavingCategory((prev) => ({ ...prev, [categoryId]: false }));
    }
  };

  const handleToggleAllCategories = async (enabled: boolean) => {
    if (!userId) return;
    const previous = { ...categoryPrefs };
    setCategoryPrefs(Object.fromEntries(categories.map((category) => [category.id, enabled])));
    try {
      await Promise.all(categories.map((category) => updateHelperCategoryPreference(userId, category.id, enabled)));
    } catch (error) {
      setCategoryPrefs(previous);
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Could not update all notification preferences.' });
    }
  };

  const handleSecurityQuestionsSave = async (event: FormEvent) => {
    event.preventDefault();
    const ids = [securityPayload.question1Id, securityPayload.question2Id, securityPayload.question3Id];
    const answers = [securityPayload.answer1, securityPayload.answer2, securityPayload.answer3];
    if (ids.some((id) => !id) || new Set(ids).size !== 3 || answers.some((answer) => answer.trim().length < 2) || !securityPayload.password) {
      showMessage({ type: 'error', text: 'Choose three different questions, enter answers, and confirm with your password.' });
      return;
    }

    setIsLoading(true);
    try {
      await updateSecurityQuestions(securityPayload);
      setSecurityPayload((prev) => ({ ...prev, password: '' }));
      showMessage({ type: 'success', text: 'Security questions updated successfully.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to save security questions.' });
    } finally {
      setIsLoading(false);
    }
  };

  const resolvedReason = useMemo(() => {
    if (!selectedReason) return null;
    if (selectedReason === 'OTHER') return customReason.trim() || null;
    return reasonOptions.find((reason) => reason.value === selectedReason)?.label || selectedReason;
  }, [customReason, selectedReason]);

  const handleDeactivateOrDelete = async () => {
    if (!selectedAction) return;
    setIsLoading(true);
    try {
      if (selectedAction === 'DELETE') {
        await createUserDeletionRequest({ deletionReason: resolvedReason });
      } else {
        await createUserSuspensionRequest({ suspensionReason: resolvedReason });
      }
      showMessage({ type: 'success', text: selectedAction === 'DELETE' ? 'Delete request submitted.' : 'Suspend request submitted.' });
      setShowConfirmDialog(false);
      await logout();
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to process request.' });
    } finally {
      setIsLoading(false);
    }
  };

  const notificationsAllEnabled = categories.length > 0 && categories.every((category) => !!categoryPrefs[category.id]);
  const lastLoginValue = user?.lastLoginAt || user?.lastLogin || '';
  const filteredLocations = locations.filter((location) => {
    const query = locationSearch.trim().toLowerCase();
    if (!query) return true;
    return `${location.country || ''} ${location.state || ''} ${location.county || ''} ${location.city || ''}`.toLowerCase().includes(query);
  });

  if (!userId) {
    return (
      <div className="settings-container">
        <div className="settings-card">Sign in to manage account settings.</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account, notifications, verification, and support settings.</p>
      </div>

      {message && <div className={`settings-message ${message.type}`}>{message.text}</div>}

      <div className="settings-content">
        <aside className="settings-sidebar">
          <SidebarSection title="Account Settings">
            <NavButton active={activeTab === 'account'} onClick={() => setActiveTab('account')}>Account Details</NavButton>
            <button className="sidebar-link" onClick={() => navigate('/account/profile-details')}>Profile Details</button>
            <button className="sidebar-link" onClick={() => navigate('/account/business/my')}>Business Entities</button>
            <NavButton active={activeTab === 'password'} onClick={() => setActiveTab('password')}>Change Password</NavButton>
            <NavButton active={activeTab === 'security'} onClick={() => setActiveTab('security')}>Security Questions</NavButton>
            <NavButton active={activeTab === 'verifyEmail'} onClick={() => setActiveTab('verifyEmail')}>Verify Email</NavButton>
            <NavButton active={activeTab === 'verifyPhone'} onClick={() => setActiveTab('verifyPhone')}>Verify Phone</NavButton>
            <NavButton active={activeTab === 'appDetails'} onClick={() => setActiveTab('appDetails')}>Olitask App Details</NavButton>
            <NavButton active={activeTab === 'supportedLocations'} onClick={() => setActiveTab('supportedLocations')}>Supported Locations</NavButton>
            <NavButton active={activeTab === 'deactivate'} onClick={() => setActiveTab('deactivate')}>Deactivate or Delete Account</NavButton>
          </SidebarSection>
          <SidebarSection title="Preferences">
            <NavButton active={activeTab === 'notifications'} onClick={() => setActiveTab('notifications')}>Notifications</NavButton>
            <NavButton active={activeTab === 'preferences'} onClick={() => setActiveTab('preferences')}>Language, Theme, Data</NavButton>
          </SidebarSection>
        </aside>

        <div className="settings-main">
          {activeTab === 'account' && (
            <form className="settings-card" onSubmit={handleAccountUpdate}>
              <h2>Account Details</h2>
              <Field label="Username"><input value={accountDetails.username} onChange={(event) => setAccountDetails((prev) => ({ ...prev, username: event.target.value }))} /></Field>
              <Field label="Email Address"><input type="email" value={accountDetails.email} onChange={(event) => setAccountDetails((prev) => ({ ...prev, email: event.target.value }))} /></Field>
              <Field label="Phone Number"><input type="tel" value={accountDetails.phone} onChange={(event) => setAccountDetails((prev) => ({ ...prev, phone: event.target.value }))} /></Field>
              <button className="btn-save" disabled={isLoading}>{isLoading ? 'Saving...' : 'Save Changes'}</button>
            </form>
          )}

          {activeTab === 'password' && (
            <form className="settings-card" onSubmit={handlePasswordUpdate}>
              <h2>Change Password</h2>
              <PasswordField
                label="Current Password"
                visible={showPasswords.current}
                value={passwordData.currentPassword}
                onToggle={() => setShowPasswords((prev) => ({ ...prev, current: !prev.current }))}
                onChange={(value) => setPasswordData((prev) => ({ ...prev, currentPassword: value }))}
              />
              <PasswordField
                label="New Password"
                visible={showPasswords.next}
                value={passwordData.newPassword}
                onToggle={() => setShowPasswords((prev) => ({ ...prev, next: !prev.next }))}
                onChange={(value) => setPasswordData((prev) => ({ ...prev, newPassword: value }))}
              />
              <PasswordField
                label="Confirm New Password"
                visible={showPasswords.confirm}
                value={passwordData.confirmPassword}
                onToggle={() => setShowPasswords((prev) => ({ ...prev, confirm: !prev.confirm }))}
                onChange={(value) => setPasswordData((prev) => ({ ...prev, confirmPassword: value }))}
              />
              <button className="btn-save" disabled={isLoading}>Update Password</button>
            </form>
          )}

          {activeTab === 'security' && (
            <form className="settings-card" onSubmit={handleSecurityQuestionsSave}>
              <h2>Manage Security Questions</h2>
              <p className="security-tip">Choose three different active security questions and confirm with your current password.</p>
              {[1, 2, 3].map((num) => (
                <div key={num} className="security-question-group">
                  <label>Question {num}</label>
                  <select
                    value={securityPayload[`question${num}Id` as keyof typeof securityPayload]}
                    onChange={(event) => setSecurityPayload((prev) => ({ ...prev, [`question${num}Id`]: event.target.value }))}
                  >
                    <option value="">Select a question...</option>
                    {securityQuestions.map((question) => (
                      <option key={question.id} value={question.id}>{question.questionText}</option>
                    ))}
                  </select>
                  <label>Answer {num}</label>
                  <input
                    type={showAnswers ? 'text' : 'password'}
                    value={securityPayload[`answer${num}` as keyof typeof securityPayload]}
                    onChange={(event) => setSecurityPayload((prev) => ({ ...prev, [`answer${num}`]: event.target.value }))}
                  />
                </div>
              ))}
              <Field label="Current Password"><input type="password" value={securityPayload.password} onChange={(event) => setSecurityPayload((prev) => ({ ...prev, password: event.target.value }))} /></Field>
              <div className="security-actions">
                <button className="btn-show-answers" type="button" onClick={() => setShowAnswers((prev) => !prev)}>{showAnswers ? 'Hide all answers' : 'Show all answers'}</button>
                <button className="btn-save" disabled={isLoading}>Save Security Questions</button>
              </div>
            </form>
          )}

          {activeTab === 'verifyEmail' && (
            <section className="settings-card verification-card">
              <h2>Verify your email address</h2>
              <p>We will send a verification code to {accountDetails.email || 'your email address'}.</p>
              {!emailOtpToken ? (
                <button className="btn-send-code" onClick={handleSendEmailVerification} disabled={isLoading}>Send Verification Code</button>
              ) : (
                <div className="verification-input-group">
                  <input value={emailCode} onChange={(event) => setEmailCode(event.target.value)} placeholder="Enter code" />
                  <button className="btn-verify" onClick={handleVerifyEmail} disabled={isLoading}>Verify</button>
                </div>
              )}
            </section>
          )}

          {activeTab === 'verifyPhone' && (
            <section className="settings-card verification-card">
              <h2>Verify your phone number</h2>
              <p>We will send a verification code to {accountDetails.phone || 'your phone number'}.</p>
              {!phoneOtpToken ? (
                <button className="btn-send-code" onClick={handleSendPhoneVerification} disabled={isLoading || !accountDetails.phone}>Send Verification Code</button>
              ) : (
                <div className="verification-input-group">
                  <input value={phoneCode} onChange={(event) => setPhoneCode(event.target.value)} placeholder="Enter code" />
                  <button className="btn-verify" onClick={handleVerifyPhone} disabled={isLoading}>Verify</button>
                </div>
              )}
            </section>
          )}

          {activeTab === 'notifications' && (
            <section className="settings-card">
              <h2>Notifications</h2>
              {!isHelper ? (
                <p className="settings-note">Email notifications are loaded from your inbox. Helper task category alerts appear here for helper accounts.</p>
              ) : (
                <>
                  <h3>Task Category Alerts</h3>
                  <NotificationToggle label="Enable All Categories" checked={notificationsAllEnabled} onChange={handleToggleAllCategories} />
                  {categories.length === 0 ? (
                    <p>No root categories found.</p>
                  ) : (
                    categories.map((category) => (
                      <NotificationToggle
                        key={category.id}
                        label={category.name}
                        checked={!!categoryPrefs[category.id]}
                        disabled={!!savingCategory[category.id]}
                        onChange={(checked) => handleToggleCategory(category.id, checked)}
                      />
                    ))
                  )}
                </>
              )}
            </section>
          )}

          {activeTab === 'preferences' && (
            <section className="settings-card">
              <h2>Language, Theme, Data</h2>
              <Field label="Language"><select value={language} onChange={(event) => setLanguage(event.target.value)}><option>English</option></select></Field>
              <Field label="Theme"><select value={theme} onChange={(event) => setTheme(event.target.value)}><option>Light</option></select></Field>
              <Field label="Data and Storage"><select value={retention} onChange={(event) => setRetention(event.target.value)}><option>N/A</option></select></Field>
            </section>
          )}

          {activeTab === 'supportedLocations' && (
            <section className="settings-card">
              <h2>Supported Locations</h2>
              <Field label="Search"><input value={locationSearch} onChange={(event) => setLocationSearch(event.target.value)} placeholder="Search country, state, county, city" /></Field>
              {isLoading ? (
                <p>Loading supported locations...</p>
              ) : (
                <div className="notification-option">
                  {filteredLocations.length === 0 ? (
                    <p>No active locations found.</p>
                  ) : (
                    filteredLocations.map((location, index) => (
                      <div className="detail-item" key={location.id || `${location.country}-${location.state}-${location.city}-${index}`}>
                        <label>{location.city || location.county || location.state || location.country || 'Supported location'}</label>
                        <p className="detail-value">{[location.city, location.county, location.state, location.country].filter(Boolean).join(', ')}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </section>
          )}

          {activeTab === 'appDetails' && (
            <section className="settings-card app-details-card">
              <h2>App Details</h2>
              <Info label="Client ID" value={localStorage.getItem('olitask_client_id') || 'Not available'} />
              <Info label="Last Login" value={lastLoginValue ? new Date(lastLoginValue).toLocaleString() : 'Not available'} />
              <Info label="App Version" value="1.0.0 (Build 150001)" />
            </section>
          )}

          {activeTab === 'deactivate' && (
            <section className="settings-card deactivate-card">
              <h2>OLITASK ACCOUNT CONTROL</h2>
              <p>You can temporarily suspend your account or permanently request deletion.</p>
              <div className="action-options">
                <label className={`action-option ${selectedAction === 'SUSPEND' ? 'selected' : ''}`}>
                  <input type="radio" checked={selectedAction === 'SUSPEND'} onChange={() => setSelectedAction('SUSPEND')} />
                  <span className="action-content"><strong>Suspend Account</strong><span>Temporarily disable access and come back later.</span></span>
                </label>
                <label className={`action-option ${selectedAction === 'DELETE' ? 'selected' : ''}`}>
                  <input type="radio" checked={selectedAction === 'DELETE'} onChange={() => setSelectedAction('DELETE')} />
                  <span className="action-content"><strong>Delete Account</strong><span>Permanently request account removal and deletion handling.</span></span>
                </label>
              </div>
              <div className="reason-section">
                <select value={selectedReason} onChange={(event) => setSelectedReason(event.target.value)}>
                  <option value="">Select a reason (optional)</option>
                  {reasonOptions.map((reason) => <option key={reason.value} value={reason.value}>{reason.label}</option>)}
                </select>
                {selectedReason === 'OTHER' && <textarea value={customReason} onChange={(event) => setCustomReason(event.target.value)} placeholder="Tell us more" />}
              </div>
              <div className="selected-summary">
                <h4>Selected action: {selectedAction || 'Not selected'}</h4>
                <p>Reason: {resolvedReason || 'Not provided'}</p>
              </div>
              <button className="btn-continue" onClick={() => setShowConfirmDialog(true)} disabled={!selectedAction}>Continue</button>
            </section>
          )}
        </div>
      </div>

      {showConfirmDialog && (
        <div className="confirm-dialog-overlay">
          <div className="confirm-dialog">
            <h3>Confirm {selectedAction === 'DELETE' ? 'Deletion' : 'Suspension'}</h3>
            <p>
              {selectedAction === 'DELETE'
                ? 'This submits an account deletion request and logs you out when successful.'
                : 'This submits an account suspension request and logs you out when successful.'}
            </p>
            <div className="dialog-actions">
              <button className="btn-cancel" onClick={() => setShowConfirmDialog(false)}>Cancel</button>
              <button className="btn-confirm" onClick={handleDeactivateOrDelete} disabled={isLoading}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function SidebarSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="sidebar-section">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

function NavButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button className={`sidebar-link ${active ? 'active' : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      {children}
    </div>
  );
}

function PasswordField({
  label,
  value,
  visible,
  onChange,
  onToggle,
}: {
  label: string;
  value: string;
  visible: boolean;
  onChange: (value: string) => void;
  onToggle: () => void;
}) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="password-input-row">
        <input type={visible ? 'text' : 'password'} value={value} onChange={(event) => onChange(event.target.value)} />
        <button type="button" className="btn-show-answers" onClick={onToggle}>{visible ? 'Hide' : 'Show'}</button>
      </div>
    </div>
  );
}

function NotificationToggle({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="notification-option">
      <label className="switch-label">
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
        <span>{label}</span>
      </label>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="detail-item">
      <label>{label}</label>
      <p className="detail-value">{value}</p>
    </div>
  );
}

export default Settings;
